<template>
  <div class="scene-node-line__group" :class="{ '-selected': selected }" @contextmenu="onContextMenu($event)">
    <div class="scene-node-line" v-if="treeNode" @click="onSelectClick($event)">
      <button
        type="button"
        class="button-transparent scene-node-line__selectbutton"
        @click.stop="onSelectClick($event)"
      ></button>
      <button
        v-if="nestable && editable"
        class="button-transparent scene-node-line__expand-icon"
        type="button"
        @click.stop="open = !open"
      >
        <lucide-icon :name="open ? 'chevron-up' : 'chevron-down'" />
      </button>
      <store-item-icon
        type="sceneNode"
        :title="getItemTypeLabel({ type: treeNode.node.dataType })"
        :item="treeNode.node"
        class="scene-node-line__icon"
      />
      <editable-text
        class="scene-node-line__title"
        ref="titleInput"
        :editable="editable"
        v-model:text="pureItemTitle"
        :displayed-text="title"
      />
      <button
        v-if="hideable"
        type="button"
        class="button-icon scene-node-line__eye"
        @click.stop="visible = !visible"
        :class="{ '-hidden': !visible }"
        :title="visible ? $t('actions.hide') : $t('actions.show')"
      >
        <lucide-icon v-if="visible" name="eye" />
        <lucide-icon v-else name="eye-off" />
      </button>
      <button
        v-if="lockable && editable"
        :disabled="superLocked"
        type="button"
        class="button-icon scene-node-line__lock"
        :class="{ '-locked': locked || superLocked }"
        @click.stop="locked = !locked"
        :title="superLocked ? t('labels.locked') : (locked ? $t('actions.unlock') : $t('actions.lock'))"
      >
        <lucide-icon v-if="superLocked" name="mouse-off" />
        <lucide-icon v-else-if="locked" name="lock" />
        <lucide-icon v-else name="lock-open" />
      </button>
    </div>

    <SortableList
      v-if="nestable && open && treeNode && editable"
      :items="childNodes"
      class="scene-node-line__children"
      item-key="uid"
      line-class="scene-node-line__child"
      :order="childNodeUids"
      :group="{ id: 'net-layers', parentUid: treeNode.uid }"
      :selected="selectedNodes.map(i => i.uid)"
      ghost-list-class="scene-node-line__children-list-ghost"
      gap="0px"
      v-slot="{ item: childNode }"
      @update:order="updateChildrenOrder($event)"
      @move-list="moveToParent($event)"
    >
      <SceneNodeLine
        :ref="(el) => childrenRefs[childNode.uid] = (el as ExposedSceneNodeLine | null)"
        :node-uid="childNode.uid"
        :editable="editable"
        :selected="selectedNodes.some((node) => node.uid === childNode.uid)"
        @click="onChildClick(childNode, $event)"
        @contextmenu="$emit('contextmenu', $event)"
        @pointerenter="highlight({ uid: childNode.uid })"
        @pointerleave="unhighlight({ uid: childNode.uid })"
      />
    </SortableList>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { splitUid, type Uid, getUid, toUid } from "@/helpers/utility/Id";
import { stripHtmlTags } from "@/helpers/utility/Text";
import { notNull } from "@/helpers/utility/Type";
import eventHub from "@/helpers/utility/Events";
import { computeMinimalOrderUpdates, getOrderValuesBetween } from "@/helpers/utility/Sorting";

import { useFlowStore } from "../flow/flows.store";
import { useModelStore } from "../model/model.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useUndo } from "@editor/store/undo/undo.store";
import { useSceneTree } from "./sceneTree";
import { useSceneNodesStore } from "./sceneNodes.store";
import { usePresenceStore } from "@editor/store/presence.store";
import { isLayerTreeNode, isSceneLayerNode } from "../sceneLayer/sceneLayerFunctions";
import { isGroupTreeNode } from "../sceneGroup/sceneGroupFunctions";
import { isSceneMediumNode } from "../sceneMedium/sceneMediumFunctions";
import { isElementTreeNode } from "../sceneElement/sceneElementFunctions";
import { isSceneTextNode } from "../sceneText/sceneTextFunctions";

import SortableList from "@core/ui/SortableList.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import StoreItemIcon from "../StoreItemIcon.vue";
import EditableText from "@core/input/EditableText.vue";

import type { ItemOfStore, ItemType } from "../ItemOfStore";
import type { ItemStoreState } from "../ItemStore";
import type { ItemStoreActions } from "../itemStore.store";
import type { SceneNodeItem } from "./SceneNodeItem";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { ChartItem } from "../charts/ChartItem";
import type { IndexItem } from "../index/IndexItem";
import type { FlowItem } from "../flow/FlowItem";

const props = withDefaults(
  defineProps<{
    nodeUid: Uid<"sceneNode">;
    selected?: boolean;
    editable?: boolean;
  }>(),
  {
    selected: false,
    editable: false
  }
);

const emit = defineEmits<{
  (e: "click", mouseEvent: MouseEvent): void;
  (e: "contextmenu", mouseEvent: MouseEvent & { item: Uid<"sceneNode">, items: { uid: Uid<"sceneNode">; id: number; type: "sceneNode" }[] }): void;
}>();

const { t } = useI18n();
const toast = useToast();
const sceneTree = useSceneTree();
const sceneNodeStore = useSceneNodesStore();
const flowStore = useFlowStore();
const selectionStore = useSelectionStore();
const undoStore = useUndo();
const presence = usePresenceStore();

const treeNode = computed(() => {
  return sceneTree.treeNodes.get(props.nodeUid);
});

const open = computed({
  get() {
    return !presence.closedSceneNodes.includes(props.nodeUid);
  },
  set(value: boolean) {
    if (!value) {
      presence.set("closedSceneNodes", [...presence.closedSceneNodes, props.nodeUid]);
    } else {
      presence.set("closedSceneNodes", presence.closedSceneNodes.filter((uid) => uid !== props.nodeUid));
    }
  }
});

type ItemStore<T extends ItemType = ItemType> = ItemStoreState<ItemOfStore<T>> &
  ItemStoreActions<ItemOfStore<T>>;

type UidEntry<T extends string> = {
  uid: Uid<T>;
  type: T;
  id: number;
};

const nestable = computed(() => {
  if (!treeNode.value) return false;
  if (isGroupTreeNode(treeNode.value)) return true;
  if (isLayerTreeNode(treeNode.value)) {
    if (treeNode.value.node.data.layerType == "links") return false;
    return true;
  }
  return false;
});
const childNodes = computed(() => {
  if (!treeNode.value) return [];
  const children = (sceneNodeStore.byParentSceneNodeUid[treeNode.value.uid] || []).slice();
  const sortedChildren = children.sort((a, b) => b.order - a.order); // Descending order
  return sortedChildren.map((child) => {
    return {
      uid: child.uid,
      type: "sceneNode" as const,
      id: child.id,
      order: child.order
    };
  });
});
const childNodeUids = computed(() => {
  return childNodes.value.map((child) => child.uid);
});

const selectedNodes = computed(() => {
  return selectionStore.selectedItems.map(({ type, id }) => {
    if (type !== "sceneNode") return null;
    return sceneTree.treeNodes.get(toUid(type, id));
  }).filter(notNull);
});

// ===========================
// LOCKING

const superLocked = computed(() => {
  if (!treeNode.value) return false;
  if (isLayerTreeNode(treeNode.value)) return false;
  return treeNode.value.superLocked || false;
});

const lockable = computed(() => {
  return true;
});
const locked = computed({
  get() {
    if (!treeNode.value) return false;
    if (!("locked" in treeNode.value)) return false;
    return treeNode.value.locked;
  },
  set(value) {
    if (!treeNode.value) return;
    sceneNodeStore.updateKey(treeNode.value.id, "locked", value);
    if (value && isLayerTreeNode(treeNode.value)) {
      // Warn that locking a layer will lock all its children
      toast.info(t("messages.lockSceneLayerWarning"));
    }
  }
});

// ===========================
// VISIBILITY

const hideable = computed(() => {
  return treeNode.value != null;
});
const visible = computed({
  get() {
    if (!treeNode.value) return true;
    if (!("visible" in treeNode.value)) return true;
    return treeNode.value.visible;
  },
  set(value) {
    if (!treeNode.value) return;
    sceneNodeStore.updateKey(treeNode.value.id, "visible", value);
  }
});

// =================
// ITEM

const connectedItem = computed(() => {
  if (!treeNode.value) return null;
  if (isElementTreeNode(treeNode.value)) {
    const elementUid = treeNode.value.node.data.elementUid;
    const { type: elementType, id: elementId } = splitUid(elementUid);
    return useModelStore().getItem(elementType, elementId);
  }
  return null;
});

// =================
// TITLE

const pureItemTitle = computed({
  get() {
    if (connectedItem.value) return connectedItem.value.title || "";
    if (!treeNode.value) return "";
    return treeNode.value.node.title || "";
  },
  set(value: string) {
    updateTitle(value);
  }
});
const title = computed({
  get() {
    if (!treeNode.value) return t("labels.unnamed");
    const node = treeNode.value.node;
    if (node && node.title) {
      return node.title;
    }
    if (connectedItem.value) return getItemTitle(connectedItem.value) || t("labels.unnamed");
    return getItemTitle(node);
  },
  set(value: string) {
    updateTitle(value);
  }
});
function getItemTitle(
  item?: SceneNodeItem | TargetItem | ResourceItem | IndexItem | ChartItem | FlowItem
) {
  if (!item) return "";
  if (item.type == "flow") {
    return flowStore.titles[item.id];
  }
  if ("title" in item && item.title) return item.title;

  if (item.type === "sceneNode") {
    if (isSceneMediumNode(item)) {
      const data = item.data;
      if ((data.mediaType || "").startsWith("image/")) {
        return t("words.image");
      }
      return t("objectNames.medium");
    }
  
    if (isSceneLayerNode(item)) {
      const data = item.data;
      if (data.layerType === "links") {
        return t("objectNames.link", 3);
      }
      if (data.layerType === "foreground") {
        return t("layerTypes.foreground")
      }
      return t("objectNames.layer");
    }

    if (isSceneTextNode(item)) {
      return stripHtmlTags(item.data.text || "");
    }

    return t(`objectNames.${item.dataType}`);
  }

  if ("text" in item && item.text && typeof item.text === "string") {
    return stripHtmlTags(item.text);
  }
  return t(`objectNames.${item.type}`);
}

function getItemTypeLabel(item?: { type: string }) {
  if (!item) return "";
  const { type } = item;
  return t(`objectNames.${type}`);
}

const titleInput = useTemplateRef("titleInput");

function startEditingTitle() {
  if (!props.editable) return;
  if (!titleInput.value) return;
  titleInput.value.startEditingText();
}
function updateTitle(newTitle: string) {
  if (!treeNode.value) return;
  if (connectedItem.value) {
    const item = connectedItem.value;
    const store = modelStore.getStore(item.type) as ItemStore<typeof item.type>;
    if (!store) return;
    store.updateKey(item.id, "title", newTitle);
    return;
  }
  sceneNodeStore.updateKey(treeNode.value.id, "title", newTitle);
}

// =================
// INTERACTION

const lastSelectedItem = ref<UidEntry<"sceneNode"> | null>(null);

function onChildClick(child: { uid: Uid<"sceneNode"> }, event: MouseEvent) {
  if (!props.editable) return;
  event.stopPropagation();
  const childNode = sceneTree.treeNodes.get(child.uid);
  if (!childNode) return;

  if (event.shiftKey && lastSelectedItem.value) {
    const childIndex = childNodes.value.findIndex((c) => c.uid === child.uid);
    const lastIndex = childNodes.value.findIndex(
      (c) => c.uid === lastSelectedItem.value?.uid
    );
    if (childIndex === -1 || lastIndex === -1) {
      selectItem({ uid: childNode.uid });
      lastSelectedItem.value = childNode;
    } else {
      // Select range
      const [start, end] =
        childIndex < lastIndex
          ? [childIndex, lastIndex]
          : [lastIndex, childIndex];
      const itemsToSelect = childNodes.value
        .slice(start, end + 1)
        .map((c) => ({ uid: c.uid }));
      selectionStore.selectMany(itemsToSelect);
    }
  } else if (event.ctrlKey || event.metaKey) {
    addItemToSelection({ uid: childNode.uid });
    lastSelectedItem.value = childNode;
  } else {
    selectItem({ uid: childNode.uid });
    lastSelectedItem.value = childNode;
  }
}

function onSelectClick(event: MouseEvent) {
  if (!props.editable) return;
  if (!treeNode.value) return;
  emit("click", event);
}

function selectItem<T extends "sceneNode" | "flow">(item: { uid: Uid<T> }) {
  if (!item) return;
  selectionStore.select(item);
}

function addItemToSelection<T extends "sceneNode" | "flow">(item: { uid: Uid<T> }) {
  if (!item) return;
  selectionStore.add(item);
}

function highlight(item: { uid: Uid<"sceneNode"> }) {
  const { type, id } = splitUid(item.uid);
  selectionStore.setHighlight({ type, id });
}
function unhighlight(item: { uid: Uid<"sceneNode"> }) {
  const { type, id } = splitUid(item.uid);
  selectionStore.removeHighlight({ type, id });
}

function onContextMenu(event: MouseEvent) {
  if (!props.editable) return;
  if (!treeNode.value) return;
  event.preventDefault();
  event.stopPropagation();

  let selection = selectedNodes.value.map((s) => getUid(s));
  const isItemSelected = selection.some(
    (uid) => uid === treeNode.value?.uid
  );
  if (!isItemSelected) {
    selection = [treeNode.value.uid];
  }

  const items = selection.map((uid) => {
    const { type, id } = splitUid(uid);
    return { uid: uid, id: id, type: type };
  });
  if (items.length === 0) return;
  emit("contextmenu", Object.assign(event, { item: treeNode.value.uid, items }));
}

// =================
// ORDER

const modelStore = useModelStore();

function moveToParent(event: {
  items: { uid: Uid<"sceneNode"> }[];
  targetIndex: number;
  targetList: { id: string; parentUid: Uid<"sceneNode"> };
  sourceList: { id: string; parentUid: Uid<"sceneNode"> };
}) {
  if (!treeNode.value) return;
  if (!("id" in treeNode.value)) return;
  if (!event.targetList) {
    console.warn("No target list for moved items", event);
    return;
  }
  const targetParentUid = event.targetList.parentUid;

  const parentChildren = (sceneNodeStore.byParentSceneNodeUid[targetParentUid] ?? []).slice();

  parentChildren.sort((a, b) => b.order - a.order); // Descending order

  const itemBefore = parentChildren[event.targetIndex - 1];
  const itemAfter = parentChildren[event.targetIndex];

  const orderValues = getOrderValuesBetween(itemBefore?.order, itemAfter?.order, event.items.length);

  undoStore.startGroup("model", "moveLayerItem");
  for (let i = 0; i < event.items.length; i++) {
    const itemToMove = event.items[i];
    const itemUid = itemToMove.uid;
    if (!itemUid) continue;
    const { id } = splitUid(itemUid);
    sceneNodeStore.update({
      id: id,
      order: orderValues[i],
      parentSceneNodeUid: targetParentUid
    });
  }
  undoStore.endGroup("model");
}

function updateChildrenOrder(newOrder: Uid<"sceneNode">[]) {
  if (!treeNode.value) return;
  if (!("id" in treeNode.value)) return;
  const reversedNewOrder = newOrder.slice().reverse();
  const childrenRecord = Object.fromEntries(childNodes.value.map((child) => [child.uid, child]));
  const updates = computeMinimalOrderUpdates(reversedNewOrder, childrenRecord);
  for (const [childUid, newOrder] of updates) {
    const { id } = splitUid(childUid);
    sceneNodeStore.updateKey(id, "order", newOrder);
  }
}

// ================
// GROUP SELECTION

type ExposedSceneNodeLine = {
  startEditingTitle: () => void;
};
const childrenRefs = ref<{ [key: string]: ExposedSceneNodeLine | null | undefined }>({});

onMounted(() => {
  eventHub.on("sceneNode-created", onSceneNodeCreated);
});
onBeforeUnmount(() => {
  eventHub.off("sceneNode-created", onSceneNodeCreated);
});
async function onSceneNodeCreated(sceneNode: SceneNodeItem) {
  // Start editing group nodes if they were just created
  if (sceneNode.dataType !== "group") return;

  // It seems to take a few ticks for the child component to be mounted and registered
  await nextTick();
  await nextTick();
  await nextTick();
  await nextTick();
  if (!childrenRefs.value[sceneNode.uid]) return;
  childrenRefs.value[sceneNode.uid]?.startEditingTitle();
}

// =================
// EXPOSE

defineExpose({
  startEditingTitle
});
</script>

<style lang="scss">
.scene-node-line {
  display: grid;
  grid-template-columns: 1.25em 2em minmax(0, 1fr) 1.5rem 1.5rem;
  grid-template-areas: "expand icon title eye lock";
  border-radius: 0.5em;
  overflow: hidden;

  &:hover {
    background-color: var(--color-background);
  }
  .-selected > & {
    background-color: var(--color-primary-light);
  }

  &__group {
    padding: 0;
    overflow: hidden;
  }

  &__expand-icon {
    grid-area: expand;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
    appearance: none;
    color: var(--color-text-light);
    transition:
      color 100ms ease-out,
      background-color 100ms ease-out;
    &:hover {
      color: var(--color-text);
      background-color: rgba(var(--tint-contrast), 0.05);
    }
  }
  &__title {
    grid-area: title;
    align-self: center;
    overflow: hidden;
  }
  &__title-text,
  &__title-input {
    padding: 0.25em 0;
    width: 100%;
    overflow: hidden;
  }
  &__title-text {
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &__title-input {
    appearance: none;
    display: block;
    border: none;
    padding: 0;
    outline: none;
    background-color: transparent;
  }

  &__icon {
    grid-area: icon;
    justify-self: center;
    align-self: center;
  }
  &__eye {
    grid-area: eye;
  }
  &__lock {
    grid-area: lock;
  }
  &__eye,
  &__lock {
    align-self: center;
    font-size: 1em;
    opacity: 0;
    transition: opacity 100ms ease-out;
  }
  &:hover &__eye,
  &:hover &__lock {
    opacity: 1;
  }
  &__eye.-hidden {
    opacity: 1;
  }
  &__lock.-locked {
    opacity: 1;
  }

  &__selectbutton {
    grid-row: 1 / -1;
    grid-column: 1 / -1;
  }

  &__children {
    padding-left: 0.4em;
    border-left: 0.2em solid var(--color-border-light);
    margin-left: 0.65em;
    min-height: 1em;
    padding-bottom: 0.125rem;
    padding-top: 0.125rem;
  }

  &__children-list-ghost {
    outline: 2px solid var(--color-primary);
    border-radius: 0.5em;
    box-shadow: 0 0.2em 0.5em var(--color-shadow-2);
    overflow: hidden;
    background-color: var(--color-background-medium);
  }

  &__children-list-ghost > &__child {
    transform: scale(1);
    pointer-events: none;
  }
  &__children-list-ghost > &__child > &__group > & {
    background-color: var(--color-background-medium);
  }
}
</style>
